import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import { useSmeAuthStore } from '@/store/sme/sme-auth';
import { useSmeCompaniesStore } from '@/store/sme/companies';
import { authRoutes } from './routes/auth';
import { smeRoutes } from './routes/sme';
import { profileRoutes } from './routes/profile';
import { companyRoutes } from './routes/company';
import { campaignRoutes } from './routes/campaign';
import { influencerRoutes } from './routes/influencer';
import { miscRoutes } from './routes/misc';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [...authRoutes, ...smeRoutes, ...profileRoutes, ...companyRoutes, ...campaignRoutes, ...influencerRoutes, ...miscRoutes],
});

// Route authentication middleware
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();
  const smeAuthStore = useSmeAuthStore();
  const smeCompaniesStore = useSmeCompaniesStore();

  // Helper function to get SME company ID and redirect
  const redirectToSmeDashboard = async () => {
    if (!smeCompaniesStore.companies?.length) {
      await smeCompaniesStore.getCompanies();
    }
    const smeCompany = smeCompaniesStore.companies.find(company => company.company_role === 'COMPANY');
    if (smeCompany) {
      next(`/sme/${smeCompany.id}/dashboard`);
    }
  };

  // Helper function to redirect to Enterprise dashboard
  const redirectToEnterpriseDashboard = () => {
    const profile = authStore.profile;
    const brandCompanies = profile.companies.filter((item: any) => item.company_role === 'BRAND');
    if (brandCompanies.length > 0) {
      next(`/companies/${brandCompanies[0].id}/dashboard`);
    } else {
      next('/companies');
    }
  };

  // Redirect for home route
  if (to.path === '/sme' && typeof to.query.token === 'string') {
    const token = to.query.token as string;
    smeAuthStore.setSmeToken(token);
    const profile = await smeAuthStore.doGetSmeProfile();
    const smeCompany = profile.companies.find((company: any) => company.company_role === 'COMPANY');

    if (smeCompany) {
      next(`/sme/${smeCompany.id}/dashboard`);
    } else {
      next(`/sme/auth/create-brand`);
    }
    return;
  }

  // Check if SME user is trying to access Enterprise routes
  if (smeAuthStore.isSmeAuthenticated && !to.path.startsWith('/sme')) {
    await redirectToSmeDashboard();
    return;
  }

  // Check if Enterprise user is trying to access SME routes
  if (authStore.isAuthenticated && to.path.startsWith('/sme')) {
    redirectToEnterpriseDashboard();
    return;
  }

  // Check for SME Anonymous requirement (SME user must NOT be logged in)
  if (to.matched.some(r => r.meta.requireSmeAnonymous === true)) {
    if (smeAuthStore.isSmeAnonymous) {
      next();
    } else {
      await redirectToSmeDashboard();
    }
    return;
  }

  // Check for SME Authentication requirement (SME user must be logged in)
  if (to.matched.some(r => r.meta.requireSmeAuthenticated === true)) {
    if (smeAuthStore.isSmeAuthenticated) {
      next();
    } else {
      next('/sme/auth/signin');
    }
    return;
  }

  // Check for Enterprise Authentication requirement
  if (to.matched.some(r => r.meta.requireAuthenticated === true)) {
    if (authStore.isAuthenticated) {
      next();
    } else {
      next('/auth');
    }
    return;
  }

  // Check for Anonymous requirement (user must NOT be logged in)
  if (to.matched.some(r => r.meta.requireAnonymous === true)) {
    if (authStore.isAnonymous) {
      next();
    } else {
      redirectToEnterpriseDashboard();
    }
    return;
  }

  // Check for Ownership requirement
  if (to.matched.some(r => r.meta.requireOwnership === true)) {
    const hasOwnership = authStore.profile?.companies.findIndex((company: any) => company.id === to.params.company_id) !== -1;
    if (hasOwnership) {
      next();
    } else {
      next('/companies');
    }
    return;
  }

  // Default: allow navigation
  next();
});

export default router;
